import React, { useEffect, useState, useRef } from 'react';
import { Button, Form, Input, Select, Radio, Divider, message, Collapse, Tooltip, InputNumber } from 'antd';
import { CloseOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { DatePicker, MobileTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import DateTimeInput from './DateTimeInput';
import useDevices from '../hooks/useDevices';
// import useElements from '../hooks/useElements';
import useEmissionPoints from '../hooks/useEmissionPoints';
import { validatePositiveDecimalGreaterThanZero } from "../utils/validators";

const { Option } = Select;

export default function PlumeMapPanel({
  visible,
  collapsed,
  position,
  onClose,
  onSubmit,
  onSelectPoint,
  selectedEmissionPointId,
  setSelectedEmissionPointId
}) {
  const { emissionPoints, reload } = useEmissionPoints();
  const [emissionRates, setEmissionRates] = useState([]);
  const [minPpmByElement, setMinPpmByElement] = useState([]);
  const [form] = Form.useForm();
  const [method, setMethod] = useState('dispositivo');
  const [timeType, setTimeType] = useState('rango');

  const { devicesWithAnemometer } = useDevices();
  // const { elements } = useElements([], true);
  // const [start, setStart] = useState(dayjs());

  useEffect(() => {
    if (!selectedEmissionPointId) return;

    const selected = emissionPoints.find(p => p.id === selectedEmissionPointId);
    if (!selected) return;

    form.setFieldsValue({
      emissionPointId: selectedEmissionPointId
    });

    // Simular el mismo comportamiento que el onChange del Select
    const anemometerDevice = devicesWithAnemometer.find(
      d => d.id === selected.anemometerDeviceId
    );

    form.setFieldsValue({
      latitud: selected.latitude.toFixed(6),
      longitud: selected.longitude.toFixed(6),
      dispositivos: anemometerDevice?.id ?? null,
      anemometerName: anemometerDevice?.name ?? null,
      sourceHeight: selected.height,
      alturaConcentracion: selected.concentrationHeight,
      umbralViento: selected.windThreshold
    });

    if (onSelectPoint) {
      onSelectPoint(selected.latitude, selected.longitude, selected.name);
    }

    // Cargar rates + minPpm igual que en onChange
    Promise.all([
      fetch(`/api/emission-points/${selectedEmissionPointId}/rates`),
      fetch(`/api/emission-points/${selectedEmissionPointId}/min-ppm`)
    ])
      .then(async ([ratesRes, minPpmRes]) => {
        const ratesData = await ratesRes.json();
        const minPpmData = await minPpmRes.json();

        setEmissionRates(ratesData);
        setMinPpmByElement(minPpmData);

        form.setFieldsValue({
          gas: null,
          emissionRate: null,
          minPpm: null
        });
      })
      .catch(() => {
        setEmissionRates([]);
        setMinPpmByElement([]);
      });

  }, [selectedEmissionPointId]);

  const emissionPointId = Form.useWatch('emissionPointId', form);
  const startTime = Form.useWatch('startTime', form);
  const exactTime = Form.useWatch('exactTime', form);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  useEffect(() => {
    if (position) {
      form.setFieldsValue({
        latitud: position.lat.toFixed(6),
        longitud: position.lng.toFixed(6),
      });
    }
  }, [position, form]);

  useEffect(() => {
    if (visible) reload();
  }, [visible]);

  useEffect(() => {
    const time = timeType === 'rango' ? startTime : exactTime;
    if (!emissionPointId || !time) return;

    const hour = dayjs(time).hour();

    fetch(`/api/emission-points/${emissionPointId}/solar-radiation/${hour}`)
      .then(res => res.json())
      .then(({ radiation }) => {
        if (radiation == null) {
          form.setFieldsValue({ radiacionSolar: null });
          message.warning(
            'No solar radiation is configured for the selected hour. Please complete the emission point configuration.'
          );
        } else {
          form.setFieldsValue({ radiacionSolar: radiation });
        }
      })
      .catch(() => {
        message.error(
          'Unable to retrieve solar radiation data. Please try again later.'
        );
      });
  }, [emissionPointId, startTime, exactTime, timeType]);

  useEffect(() => {
    if (!visible) {
      // 1. Reset formulario AntD
      form.resetFields();

      // 2. Reset estados locales
      setEmissionRates([]);
      setMethod('dispositivo');
      setTimeType('rango');
    }
  }, [visible, form]);

  const handleSubmit = (values) => {
    
    const sourceHeight = parseFloat(values.sourceHeight);
    const concentrationHeight = parseFloat(values.alturaConcentracion);

    if (Math.abs(sourceHeight - concentrationHeight) < 0.1) {
      values.sourceHeight = (sourceHeight - 0.1).toFixed(2);
    }

    if (values.minPpm <= 0) {
      values.minPpm = 0.000001;
    }

    if (values.umbralViento <= 0) {
      values.umbralViento = 0.1;
    }

    if (values.metodo === 'manual') {
      values.temp = values.temp || null;
      values.presion = values.presion || null;
      values.viento = values.viento || null;
      values.windVane = values.windVane || null;
      values.radiacionSolar = values.radiacionSolar || null;
      values.nublado = values.nublado === "yes";
    }

    if (!values.latitud || !values.longitud) {
      message.error('Please select a point on the map');
      return;
    }

    onSubmit(values);
  };

  if (!visible || collapsed) return null;

  return (
    <div
      style={{
        position: 'absolute',
        top: 64,
        right: 0,
        width: 400,
        height: 'calc(100vh - 64px)',
        backgroundColor: '#ffffff',
        boxShadow: '-2px 0 10px rgba(0, 0, 0, 0.3)',
        zIndex: 1000,
        overflowY: 'auto',
        overflowX: 'hidden',
        borderLeft: '4px solid #0075C9',
        pointerEvents: 'auto',
        transition: 'transform 0.3s ease-in-out',
        transform: collapsed ? 'translateX(100%)' : 'translateX(0)',
      }}
    >
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 20,
          background: '#fff',     // Para que no desaparezca al hacer scroll
          padding: '10px 15px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          // borderBottom: '1px solid #eee'
        }}
      >
        <h2 style={{ color: '#0075C9' }}>Plume Dispersion</h2>  
        <Button
          onClick={() => {
            form.resetFields();
            onClose();
          }}
          icon={<CloseOutlined />}
          shape="circle"
          size="small"
          style={{
            backgroundColor: '#0077b6',
            color: 'white',
            border: 'none',
          }}
        />
      </div>
      <div style={{ padding: "0 15px" }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              latitud: position?.lat?.toFixed(6),
              longitud: position?.lng?.toFixed(6),
              startDate: null,
              startTime: null,
              exactDate: null,
              exactTime: null,
              metodo: 'dispositivo',
              tipoTiempo: 'rango'
            }}
          >
            <Form.Item label="Emission point" name="emissionPointId">
              <Select
                placeholder="Select a predefined point"
                allowClear
                onChange={async (pointId) => {
                  setSelectedEmissionPointId?.(pointId); 
                  const selected = emissionPoints.find(p => p.id === pointId);
                  const anemometerDevice = devicesWithAnemometer.find(d => d.id === selected.anemometerDeviceId);
                  // console.log(selected)
                  if (selected) {
                    form.setFieldsValue({
                      latitud: selected.latitude.toFixed(6),
                      longitud: selected.longitude.toFixed(6),
                      dispositivos: anemometerDevice?.id ?? null,
                      anemometerName: anemometerDevice?.name ?? null,
                      sourceHeight: selected.height,
                      alturaConcentracion: selected.concentrationHeight,
                      // minPpm: selected.minPpm,
                      umbralViento: selected.windThreshold
                    });

                    if (onSelectPoint) {
                      onSelectPoint(selected.latitude, selected.longitude, selected.name);
                    }

                    try {
                      const [ratesRes, minPpmRes] = await Promise.all([
                        fetch(`/api/emission-points/${pointId}/rates`),
                        fetch(`/api/emission-points/${pointId}/min-ppm`)
                      ]);

                      const ratesData = await ratesRes.json();
                      const minPpmData = await minPpmRes.json();

                      setEmissionRates(ratesData);
                      setMinPpmByElement(minPpmData);

                      form.setFieldsValue({
                        gas: null,
                        emissionRate: null,
                        minPpm: null
                      });

                    } catch (err) {
                      console.error('Error loading emission point data', err);
                      setEmissionRates([]);
                      setMinPpmByElement([]);
                      form.setFieldsValue({
                        gas: null,
                        emissionRate: null,
                        minPpm: null
                      });
                    }
                  }
                }}
              >
                {emissionPoints.map(p => (
                  <Select.Option key={p.id} value={p.id}>
                    {p.name || `Point ${p.id}`}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item label="Latitude" name="latitud" hidden><Input disabled /></Form.Item>
            <Form.Item label="Longitude" name="longitud" hidden><Input disabled /></Form.Item>

            <Form.Item label="Gas" name="gas" rules={[{ required: true, message: 'Select a gas' }]}>
              <Select
                placeholder="Select a gas"
                onChange={(selectedGas) => {
                  const rate = emissionRates.find(r => r.elementName === selectedGas);
                  const minPpmEntry = minPpmByElement.find(
                    m => m.elementId === rate?.elementId
                  );

                  form.setFieldsValue({
                    emissionRate: rate?.emissionRate ?? null,
                    minPpm: minPpmEntry?.minPpm ?? null
                  });

                  if (!minPpmEntry) {
                    message.warning(
                      "No minimum PPM configured for this gas. Please check the emission point configuration."
                    );
                  }
                }}
              >
                {emissionRates.map(rate => (
                  <Option key={rate.elementId} value={rate.elementName}>
                    {rate.elementName}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label="Emission rate (kg/h)" name="emissionRate" rules={[{ required: true }]}>
              <Input disabled />
            </Form.Item>

            <Form.Item label="Time type" name="tipoTiempo">
              <Radio.Group onChange={e => setTimeType(e.target.value)}>
                <Radio value="rango">Range</Radio>
                <Radio value="puntual">Exact time</Radio>
              </Radio.Group>
            </Form.Item>

            {timeType === 'rango' ? (
              <>
                <Form.Item label="From" required rules={[{ required: true }]}>
                  <DateTimeInput dateName={"startDate"} timeName={"startTime"}/>
                </Form.Item>
                <Form.Item label="Duration" name="duration" initialValue={30} rules={[{ required: true }]}>
                  <Select>
                    <Option value={30}>30 minutes</Option>
                    <Option value={60}>1 hour</Option>
                    <Option value={120}>2 hours</Option>
                  </Select>
                </Form.Item>
              </>
            ) : (
              <Form.Item label="Exact datetime" required>
                <DateTimeInput dateName={"exactDate"} timeName={"exactTime"}/>
              </Form.Item>
            )}

            <Collapse
              className="advanced-collapse"
              bordered={false}
              style={{ background: 'transparent', marginTop: 20 }}
              onChange={(keys) => setAdvancedOpen(keys.includes('advanced'))}
              items={[
                {
                  key: 'advanced',
                  label: 'Advanced settings',
                  style: { padding: 0 },
                  forceRender: true,
                  children: (
                    <div style={{ padding: 0 }}>
                      <Form.Item
                        label={
                          <span>
                            Source height (m)
                            <Tooltip title="Vertical distance from the ground to the emission point.">
                              <InfoCircleOutlined style={{ marginLeft: 6 }} />
                            </Tooltip>
                          </span>
                        }
                        name="sourceHeight"
                        rules={ advancedOpen ? [{ required: true, message: "Source height is required" }] : [] }
                      >
                        <InputNumber style={{ width: "100%" }} disabled />
                      </Form.Item>

                      <Form.Item
                        label={
                          <span>
                            Concentration height (m)
                            <Tooltip title="Elevation above ground where pollution is calculated (1.5–2m = human breathing level).">
                              <InfoCircleOutlined style={{ marginLeft: 6 }} />
                            </Tooltip>
                          </span>
                        }
                        name="alturaConcentracion"
                        rules={ advancedOpen ?[{ required: true, message: "Concentration height is required" }] : [] }
                      >
                        <InputNumber style={{ width: "100%" }} step="any" />
                      </Form.Item>

                      <Form.Item
                        label={
                          <span>
                            Min ppm
                            <Tooltip title="Minimum concentration visible on the map. Values below this threshold are hidden to reduce visual clutter.">
                              <InfoCircleOutlined style={{ marginLeft: 6 }} />
                            </Tooltip>
                          </span>
                        }
                        name="minPpm"
                        rules={ advancedOpen ?[{ required: true, message:"Minimun concentration is required" }, { validator: validatePositiveDecimalGreaterThanZero }] : [] }
                      >
                        <InputNumber style={{ width: "100%" }} step="any" />
                      </Form.Item>

                      <Form.Item
                        label={
                          <span>
                            Wind threshold (mph)
                            <Tooltip title="Minimum wind speed required to generate the plume model.">
                              <InfoCircleOutlined style={{ marginLeft: 6 }} />
                            </Tooltip>
                          </span>
                        }
                        name="umbralViento"
                        rules={ advancedOpen ?[{ required: true, message: "Wind threshold is required" }, { validator: validatePositiveDecimalGreaterThanZero }] : [] }
                      >
                        <InputNumber style={{ width: "100%" }} step="any" />
                      </Form.Item>

                      <Divider>Meteorological data</Divider>

                      <Form.Item label="Method" name="metodo">
                        <Radio.Group onChange={e => setMethod(e.target.value)}>
                          <Radio value="dispositivo">From device</Radio>
                          <Radio value="manual">Manual</Radio>
                        </Radio.Group>
                      </Form.Item>

                      {method === 'dispositivo' && (
                        <>
                          <Form.Item label="Device with anemometer" name="anemometerName" rules={[{ required: true }]}>
                            <Input disabled />
                          </Form.Item>
                          <Form.Item name="dispositivos" hidden>
                            <Input />
                          </Form.Item>
                        </>
                      )}

                      {method === 'manual' && (
                        <>
                          <Form.Item label="Temperature (°C)" name="temp"><InputNumber style={{ width: "100%" }} step="any" /></Form.Item>
                          <Form.Item label="Pressure (mb)" name="presion"><InputNumber style={{ width: "100%" }} step="any" /></Form.Item>
                          <Form.Item label="Wind speed (mph)" name="viento"><InputNumber style={{ width: "100%" }} step="any" /></Form.Item>
                          <Form.Item label="Wind vane (°)" name="windVane"><InputNumber style={{ width: "100%" }} step="any" /></Form.Item>
                        </>
                      )}

                      {(method === 'manual' || method === 'dispositivo') && (
                        <>
                          <Form.Item
                            label={
                              <span>
                                Solar radiation (W/m²)
                                <Tooltip title="Solar intensity used for atmospheric stability: 0-50: Night · 50-350: Slight · 350-700: Moderate · >700: Strong">
                                  <InfoCircleOutlined style={{ marginLeft: 6 }} />
                                </Tooltip>
                              </span>
                            }
                            name="radiacionSolar"
                            rules={ advancedOpen ?[{ required: true, message: "Solar radiation is required" }] : [] }
                          >
                            <InputNumber style={{ width: "100%" }} step="any" />
                          </Form.Item>

                          <Form.Item
                            label={
                              <span>
                                Cloudy
                                <Tooltip title="Cloudiness affects heat retention and atmospheric turbulence.">
                                  <InfoCircleOutlined style={{ marginLeft: 6 }} />
                                </Tooltip>
                              </span>
                            }
                            name="nublado"
                            initialValue="no"
                          >
                            <Select>
                              <Option value="yes">Yes</Option>
                              <Option value="no">No</Option>
                            </Select>
                          </Form.Item>
                        </>
                      )}
                    </div>
                  )
                }
              ]}
            />
            <Form.Item>
              <Button
                type="primary"
                style={{
                  width: '100%',
                  background: '#0075C9',
                  color: 'white',
                  marginTop: 20
                }}
                onClick={async () => {
                  try {
                    const values = await form.validateFields();
                    handleSubmit(values);
                  } catch (err) {
                    message.error("Please fill in all required fields.");

                    const first = err.errorFields?.[0]?.name;
                    if (first) {
                      // Scroll al campo, pero centrándolo para que NO quede tapado
                      form.scrollToField(first, {
                        behavior: "smooth",
                        block: "center"
                      });
                    }
                  }
                }}
              >
                Apply
              </Button>
            </Form.Item>
          </Form>
        </LocalizationProvider>
      </div>
    </div>
  );
}
